import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Table,
  Dropdown,
  Menu,
  Checkbox,
  Spin,
  DatePicker,
  Select,
  Empty,
  Tooltip,
  message,
} from "antd";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import type { Dayjs } from "dayjs";
import { useProcessContext } from "../../context/ProcessContext";
import useGetLogs from "../../hooks/getLogs/useGetLogs";
import useGetProcess from "../../hooks/getProcess/useGetProcess";
import useGetDetailLogs from "../../hooks/getDetailLog/useGetDetailLog";
import { TimeFormatter } from "helpers/TimeFormatter/TimeFormatter";
import { useNavigate } from "react-router-dom";
import {
  ClearOutlined,
  DownOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import "./LogsPage.css";
import ExportTableButton from "layouts/LayoutTableExport/LayoutTableExport";
import {
  getProcessDisplayName,
  getProcessMenuOrder,
} from "../../utils/processLabels";
import TableSkeleton from "../../ui/table/TableSkeleton";
import { TABLE_PAGINATION_UX } from "../../ui/table/paginationDefaults";
import LogStatusTag, {
  getLogStatusLabel,
} from "../../components/LogStatusTag/LogStatusTag";
import CopyableTruncatedCell from "../../components/CopyableTruncatedCell";

interface Logs {
  id: number;
  processName: string;
  executionStatus: string;
  creationDate: string;
  totalItems: string;
  failedItems: number;
  actions: unknown;
}

const LOG_EXECUTION_STATUS_OPTIONS = [
  { value: "", label: "Todos (estado)" },
  { value: "SUCCESSFUL", label: "Exitoso" },
  { value: "FAILED", label: "Fallido" },
];

const renderColumnTitle = (label: string, tooltip?: string) => (
  <Tooltip title={tooltip ?? label} placement="top">
    <span className="logs-page__th-label">{label}</span>
  </Tooltip>
);

const renderDateTime = (value: string | null | undefined) => {
  if (!value) return "—";
  const formatted = TimeFormatter(value);
  if (formatted === "Invalid date") return value;
  const [datePart, timePart] = formatted.split(" ");
  return (
    <span className="logs-page__datetime" title={formatted}>
      <span className="logs-page__datetime-date">{datePart}</span>
      {timePart ? (
        <span className="logs-page__datetime-time">{timePart}</span>
      ) : null}
    </span>
  );
};

const LogsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedProcessType, setSelectedProcessType, setLogId } =
    useProcessContext();
  const { dataProcess, loading: loadingProcess, fetchProcess } =
    useGetProcess();
  // setDatalogs intentionally unused — list is driven only by fetchLogs
  const { datalogs, loading: fetching, fetchLogs, totalElements } =
    useGetLogs();
  const { fetchDetailLog } = useGetDetailLogs();
  const [loading, setLoading] = useState(false);
  const [executionStatus, setExecutionStatus] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  const lookFilters = useMemo(() => {
    const fromDate = dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : "";
    const toDate = dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : "";
    return {
      fromDate,
      toDate,
      status: executionStatus,
    };
  }, [dateRange, executionStatus]);

  const isInvalidDateRange = Boolean(
    lookFilters.fromDate &&
      lookFilters.toDate &&
      lookFilters.fromDate > lookFilters.toDate
  );

  const hasProcessSelected = selectedProcessType > 0;

  const processOptions = useMemo(() => {
    const options = (dataProcess ?? []).map((process) => ({
      label: getProcessDisplayName(process.name) || process.name,
      value: process.id,
    }));

    return options.sort(
      (a, b) => getProcessMenuOrder(a.label) - getProcessMenuOrder(b.label)
    );
  }, [dataProcess]);

  const [selectedColumns, setSelectedColumns] = useState<React.Key[]>([
    "creationDate",
    "executionStatus",
    "processName",
    "totalItems",
    "failedItems",
    "actions",
  ]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const isBusy = loading || fetching;

  const handleProcessChange = (value: string | number) => {
    const processId = Number(value);
    if (!processId) return;

    if (
      lookFilters.fromDate &&
      lookFilters.toDate &&
      lookFilters.fromDate > lookFilters.toDate
    ) {
      message.error(
        "Rango de fechas inválido: la fecha inicio no puede ser mayor que la fin."
      );
      return;
    }

    setSelectedProcessType(processId);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchLogs(processId, 1, pagination.pageSize || 10, lookFilters);
  };

  const handleViewDetails = async (
    logId: number,
    processType: number,
    executionStatus: string
  ) => {
    if (!processType) return;

    // ASSUMPTION: Opening a FAILED run prefilters detail items by their own (item) status,
    // not the batch's execution_status — otherwise OK items look like errors in the detail view.
    const initialItemStatus =
      executionStatus?.toUpperCase() === "FAILED" ? "FAILED" : "";

    try {
      await fetchDetailLog(logId.toString(), processType, 1, 10, {
        onlyFailed: false,
        itemStatus: initialItemStatus || undefined,
      });
      setLogId(logId);
      navigate(`/logs/${logId}`, { state: { initialItemStatus } });
    } catch (error) {
      console.error("Error fetching log details:", error);
    }
  };

  const allColumns: TableColumnsType<Logs> = [
    {
      title: renderColumnTitle("F. Creación", "Fecha de Creación"),
      dataIndex: "creationDate",
      key: "creationDate",
      width: 110,
      render: (value: string) => renderDateTime(value),
    },
    {
      title: renderColumnTitle("Estado"),
      dataIndex: "executionStatus",
      key: "executionStatus",
      width: 110,
      render: (value: string) => <LogStatusTag status={value} />,
    },
    {
      title: renderColumnTitle("Proceso"),
      dataIndex: "processName",
      key: "processName",
      width: 120,
      render: (value: string) => {
        const label = getProcessDisplayName(value);
        return (
          <CopyableTruncatedCell
            value={label}
            copySuccessMessage="Proceso copiado"
            onlyWhenTruncated={false}
          />
        );
      },
    },
    {
      title: renderColumnTitle("Total", "Total Items"),
      dataIndex: "totalItems",
      key: "totalItems",
      width: 88,
    },
    {
      title: renderColumnTitle("Fallidos", "Failed Items"),
      dataIndex: "failedItems",
      key: "failedItems",
      width: 96,
      render: (value: number) => (
        <span
          className={
            value > 0
              ? "logs-page__failed-count logs-page__failed-count--warn"
              : "logs-page__failed-count"
          }
        >
          {value ?? 0}
        </span>
      ),
    },
    {
      title: renderColumnTitle("Acciones"),
      dataIndex: "actions",
      key: "actions",
      width: 140,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          className="logs-page__detail-btn"
          icon={<InfoCircleOutlined />}
          disabled={isBusy}
          onClick={() =>
            handleViewDetails(
              record.id,
              selectedProcessType,
              record.executionStatus
            )
          }
        >
          Ver detalle
        </Button>
      ),
    },
  ];

  const filteredColumns = allColumns.filter((column) =>
    selectedColumns.includes(column.key!)
  );

  const handleColumnSelection = (columnKey: React.Key) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleClearFilters = () => {
    setExecutionStatus("");
    setDateRange([null, null]);
    setPagination((prev) =>
      prev.current === 1 ? prev : { ...prev, current: 1 }
    );
  };

  const onReload = () => {
    if (!hasProcessSelected) return;
    if (isInvalidDateRange) {
      message.error(
        "Rango de fechas inválido: la fecha inicio no puede ser mayor que la fin."
      );
      return;
    }

    setLoading(true);
    fetchLogs(
      selectedProcessType,
      pagination.current || 1,
      pagination.pageSize || 10,
      lookFilters
    ).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProcess();
  }, [fetchProcess]);

  // Keep selected process when returning from detail (Volver).
  useEffect(() => {
    setLogId(null);
  }, [setLogId]);

  // Restore table when process is already selected in context.
  useEffect(() => {
    if (selectedProcessType <= 0) return;
    if (isInvalidDateRange) {
      message.error(
        "Rango de fechas inválido: la fecha inicio no puede ser mayor que la fin."
      );
      return;
    }

    fetchLogs(
      selectedProcessType,
      pagination.current || 1,
      pagination.pageSize || 10,
      lookFilters
    );
    // Intentionally run when process/filters change or page mounts with a selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcessType, fetchLogs, lookFilters]);

  const formattedDataLogs = datalogs?.map((item) => ({
    ...item,
    creationDate: item.creationDate,
    executionStatusLabel: getLogStatusLabel(item.executionStatus),
  }));

  const exportColumns = useMemo(
    () =>
      [
        { key: "creationDate", title: "Fecha de Creación" },
        { key: "executionStatusLabel", title: "Estado" },
        { key: "processName", title: "Proceso" },
        { key: "totalItems", title: "Total Items" },
        { key: "failedItems", title: "Fallidos" },
      ].filter((col) =>
        selectedColumns.includes(
          col.key === "executionStatusLabel" ? "executionStatus" : col.key
        )
      ),
    [selectedColumns]
  );

  const exportData = useMemo(
    () =>
      (datalogs ?? []).map((item) => ({
        creationDate: TimeFormatter(item.creationDate),
        executionStatusLabel: getLogStatusLabel(item.executionStatus),
        processName: getProcessDisplayName(item.processName),
        totalItems: item.totalItems,
        failedItems: item.failedItems,
      })),
    [datalogs]
  );

  const handleTableChange = (nextPagination: TablePaginationConfig) => {
    if (!hasProcessSelected) return;
    if (isInvalidDateRange) {
      message.error(
        "Rango de fechas inválido: la fecha inicio no puede ser mayor que la fin."
      );
      return;
    }

    setPagination(nextPagination);
    fetchLogs(
      selectedProcessType,
      nextPagination.current || 1,
      nextPagination.pageSize || 10,
      lookFilters
    );
  };

  const menu = (
    <Menu>
      {[
        { key: "creationDate", label: "Fecha de Creación" },
        { key: "executionStatus", label: "Estado" },
        { key: "processName", label: "Proceso" },
        { key: "totalItems", label: "Total Items" },
        { key: "failedItems", label: "Fallidos" },
        { key: "actions", label: "Acciones" },
      ].map((column) => (
        <Menu.Item key={column.key}>
          <Checkbox
            checked={selectedColumns.includes(column.key)}
            onChange={() => handleColumnSelection(column.key)}
          >
            {column.label}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: totalElements }));
  }, [totalElements]);

  return (
    <>
      <div className="page__content">
        {!hasProcessSelected && (
          <section className="logs-page__process-card">
            <div className="logs-page__process-card-text">
              <h2 className="logs-page__process-title">
                ¿Qué proceso quieres consultar?
              </h2>
              <p className="logs-page__process-subtitle">
                Toca una opción para cargar sus logs.
              </p>
              <p className="logs-page__field-hint logs-page__field-hint--center">
                Los logs se consultan por proceso (Órdenes, Catálogo,
                Inventario o Precios).
              </p>
            </div>

            <Spin spinning={loadingProcess}>
              {processOptions.length > 0 ? (
                <div
                  className="logs-page__process-options"
                  role="group"
                  aria-label="Procesos disponibles"
                >
                  {processOptions.map((process) => (
                    <button
                      key={process.value}
                      type="button"
                      className="logs-page__process-option"
                      aria-pressed={false}
                      onClick={() => handleProcessChange(process.value)}
                    >
                      {process.label}
                    </button>
                  ))}
                </div>
              ) : (
                !loadingProcess && (
                  <p className="logs-page__process-empty">
                    No hay procesos disponibles.
                  </p>
                )
              )}
            </Spin>
          </section>
        )}

        {hasProcessSelected && (
          <>
            <div className="logs-page__toolbar">
              <div className="logs-page__toolbar-group logs-page__toolbar-group--actions">
                <span className="logs-page__toolbar-label">Acciones</span>
                <div className="logs-page__toolbar-control-row">
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={onReload}
                    loading={isBusy}
                  >
                    {isBusy ? "Actualizando" : "Actualizar"}
                  </Button>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                    disabled={isBusy}
                  >
                    Limpiar filtros
                  </Button>
                  <ExportTableButton
                    data={exportData}
                    columns={exportColumns}
                    disabled={isBusy || exportData.length === 0}
                    variant="default"
                  />
                  <Dropdown
                    overlay={menu}
                    trigger={["click"]}
                    disabled={isBusy}
                  >
                    <Button
                      className="logs-page__toolbar-columns-btn"
                      disabled={isBusy}
                    >
                      Columnas <DownOutlined />
                    </Button>
                  </Dropdown>
                </div>
              </div>

              <div
                className="logs-page__toolbar-separator"
                aria-hidden="true"
              />

              <div className="logs-page__toolbar-group logs-page__toolbar-group--filters">
                <span className="logs-page__toolbar-label logs-page__toolbar-label--section">
                  Filtros
                </span>
                <div className="logs-page__toolbar-filters-grid">
                  <div className="logs-page__toolbar-field logs-page__toolbar-field--process">
                    <span className="logs-page__toolbar-label">Proceso</span>
                    <Spin spinning={loadingProcess}>
                      <div
                        className="logs-page__process-options logs-page__process-options--toolbar"
                        role="group"
                        aria-label="Procesos disponibles"
                      >
                        {processOptions.map((process) => {
                          const isActive =
                            selectedProcessType === process.value;

                          return (
                            <button
                              key={process.value}
                              type="button"
                              className={`logs-page__process-option ${
                                isActive
                                  ? "logs-page__process-option--active"
                                  : ""
                              }`}
                              aria-pressed={isActive}
                              disabled={isBusy}
                              onClick={() =>
                                handleProcessChange(process.value)
                              }
                            >
                              {process.label}
                            </button>
                          );
                        })}
                      </div>
                    </Spin>
                    <p className="logs-page__field-hint">
                      Cada proceso muestra solo sus ejecuciones. Cambia de
                      proceso para consultar otro listado.
                    </p>
                  </div>

                  <div className="logs-page__toolbar-field logs-page__toolbar-field--dates">
                    <span className="logs-page__toolbar-label">Fechas</span>
                    <DatePicker.RangePicker
                      className="logs-page__date-range"
                      value={dateRange}
                      placeholder={["Desde", "Hasta"]}
                      format="YYYY-MM-DD"
                      disabled={isBusy}
                      onChange={(values) => {
                        setDateRange(
                          values
                            ? [values[0] ?? null, values[1] ?? null]
                            : [null, null]
                        );
                        setPagination((prev) =>
                          prev.current === 1 ? prev : { ...prev, current: 1 }
                        );
                      }}
                    />
                  </div>

                  <div className="logs-page__toolbar-field logs-page__toolbar-field--status">
                    <span className="logs-page__toolbar-label">Estado</span>
                    <Select
                      className="logs-page__status-select"
                      value={executionStatus}
                      options={LOG_EXECUTION_STATUS_OPTIONS}
                      disabled={isBusy}
                      onChange={(value: string) => {
                        setExecutionStatus(value);
                        setPagination((prev) =>
                          prev.current === 1 ? prev : { ...prev, current: 1 }
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {fetching && (!datalogs || datalogs.length === 0) ? (
              <TableSkeleton />
            ) : (
              <div className="data-enter">
                <Table<Logs>
                  className="logs-page__table"
                  columns={filteredColumns}
                  dataSource={formattedDataLogs || []}
                  loading={fetching}
                  rowKey="id"
                  locale={{
                    emptyText: (
                      <Empty
                        description={
                          executionStatus || dateRange[0] || dateRange[1] ? (
                            <span>
                              Sin logs para los filtros actuales.
                              <br />
                              <Button
                                type="link"
                                onClick={handleClearFilters}
                                disabled={isBusy}
                              >
                                Limpiar filtros
                              </Button>
                            </span>
                          ) : (
                            "No hay logs para este proceso."
                          )
                        }
                      />
                    ),
                  }}
                  pagination={{
                    ...pagination,
                    ...TABLE_PAGINATION_UX,
                    total: totalElements,
                    showTotal: (total) =>
                      `${total.toLocaleString("es-CO")} ${total === 1 ? "log" : "logs"}`,
                  }}
                  onChange={handleTableChange}
                  scroll={{ x: 780 }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default LogsPage;
